import {
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { getTraceHistorySample } from 'api/snapshotApi';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { startOfToday } from 'date-fns';
import { analyzerChanged } from 'features/analyzers/analyzerSlice';
import { useAPICall } from 'hooks/useAPICall';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TraceHistorySample } from 'types';
import SettingsTabPanel from 'features/singleTraceView/SettingsTabPanel';
import { grey } from '@mui/material/colors';
import WeatherReflectionFilters from './WeatherReflectionFilters';
import WeatherReflectionConstraints from './WeatherReflectionConstraints';
import { SubmittedReflectionFormValues, WeatherConstraint } from './types';
import { useParseQueryString } from 'features/traceHistory/hooks/useParseQueryString';
import {
  convertNumberFromDB,
  convertToImperial,
} from 'features/weather/utils/dataConverters';
import { MeasurementData } from 'features/weather/types';
import WeatherTraceDisplay from './WeatherTraceDisplay';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useLocationByAnalyzer } from 'features/locations/api/getLocationByAnalyzer';
import { useTimestamps } from 'features/weather/api/getTimestamps';
import { convertConstraintsForAPICall } from './utils/constraintConverters';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

const WeatherReflectionPage = () => {
  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const { timezone, getCurrentDateTime } = useUserDateTime();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      fromDate: startOfToday(),
      toDate: getCurrentDateTime(),
      analyzerId: -1,
      snapshotId: -1,
    },
  });

  const [weatherConstraints, setWeatherConstraints] = useState<
    WeatherConstraint[]
  >([
    {
      measurement: '',
      level: '0',
      greaterThan: true,
    },
  ]);

  const {
    hasQueryString,
    startTime,
    endTime,
    snapshotIdToLoad,
    analyzerIdToLoad,
    traceIndex: queryTraceIndex,
    weatherConstraints: queryWeatherConstraints,
  } = useParseQueryString(isSubmitted);

  const { data: locationForAnalyzer } = useLocationByAnalyzer({
    analyzerId:
      watch('analyzerId') !== -1 ? watch('analyzerId') : analyzerIdToLoad,
  });

  const [submittedFormValues, setSubmittedFormValues] =
    useState<SubmittedReflectionFormValues>({
      analyzerId: -1,
      snapshotId: -1,
      constraints: {
        begin: zonedTimeToUtc(startOfToday(), timezone).toISOString(),
        end: zonedTimeToUtc(new Date(), timezone).toISOString(),
        constraints: [],
      },
    });

  const {
    isLoading,
    refetch,
    data: timeline,
  } = useTimestamps({
    locationId: locationForAnalyzer?.id,
    snapshotId: submittedFormValues.snapshotId,
    constraints: {
      begin: submittedFormValues.constraints.begin,
      end: submittedFormValues.constraints.end,
      constraints: submittedFormValues.constraints.constraints,
    },
  });

  const [traceIndex, setTraceIndex] = useState(0);
  const [hideFilters, setHideFilters] = useState<boolean>(false);

  const [currentTrace, setCurrentTrace] = useState<
    TraceHistorySample | undefined
  >(undefined);
  const { safeCall: safeGetTraceHistory } = useAPICall(getTraceHistorySample);

  const handleTraceIndexChanged = (value: any) => {
    if (!timeline) return;
    if (typeof value === 'number') {
      if (value < timeline.length) setTraceIndex(value);
    }
  };

  useEffect(() => {
    const fetchTraceSample = async () => {
      if (!timeline || !timeline[traceIndex]?.time) return;
      const sample = await safeGetTraceHistory(
        watch('snapshotId'),
        timeline[traceIndex].time
      );
      setCurrentTrace(sample);
    };
    fetchTraceSample();
  }, [watch, traceIndex, safeGetTraceHistory, timeline]);

  const onSubmit = async () => {
    if (!locationForAnalyzer) return;
    const formState = getValues();
    dispatch(analyzerChanged(formState.analyzerId));
    setTraceIndex(0);
    setSubmittedFormValues({
      analyzerId: formState.analyzerId,
      snapshotId: formState.snapshotId,
      constraints: {
        begin: zonedTimeToUtc(formState.fromDate, timezone).toISOString(),
        end: zonedTimeToUtc(formState.toDate, timezone).toISOString(),
        constraints: convertConstraintsForAPICall(
          weatherConstraints,
          inImperial
        ),
      },
    });
  };

  // Populate form fields with the query string values, if available
  useEffect(() => {
    const initializePage = async () => {
      if (!locationForAnalyzer || !hasQueryString || !startTime || !endTime)
        return;

      setValue('fromDate', new Date(startTime));
      setValue('toDate', new Date(endTime));
      setValue('analyzerId', analyzerIdToLoad);
      setValue('snapshotId', snapshotIdToLoad);
      if (queryWeatherConstraints.length > 0)
        setWeatherConstraints(
          queryWeatherConstraints.map((constraint) => ({
            ...constraint,
            level:
              // @ts-ignore
              convertNumberFromDB(
                convertToImperial(
                  Number(constraint.level),
                  constraint.measurement as MeasurementData,
                  inImperial
                ),
                constraint.measurement as MeasurementData
              ).toFixed(2),
          }))
        );
      else
        setWeatherConstraints([
          {
            measurement: '',
            level: '0',
            greaterThan: true,
          },
        ]);
      dispatch(analyzerChanged(analyzerIdToLoad));
      setTraceIndex(queryTraceIndex);
      setSubmittedFormValues({
        analyzerId: analyzerIdToLoad,
        snapshotId: snapshotIdToLoad,
        constraints: {
          begin: startTime,
          end: endTime,
          constraints: queryWeatherConstraints,
        },
      });
    };
    initializePage();
  }, [
    refetch,
    safeGetTraceHistory,
    queryWeatherConstraints,
    inImperial,
    setValue,
    analyzerIdToLoad,
    dispatch,
    endTime,
    hasQueryString,
    locationForAnalyzer,
    snapshotIdToLoad,
    startTime,
    queryTraceIndex,
  ]);

  return (
    <Grid
      container
      spacing={2}
      direction='column'
      sx={{
        minHeight: 'calc(93vh - 64px)',
        position: 'relative',
        paddingBottom: '1.5rem',
      }}
      wrap='nowrap'
    >
      <IconButton
        sx={{
          showHideFilterIcon: {
            position: 'absolute',
            right: 10,
            top: 27,
          },
        }}
        onClick={() => setHideFilters(!hideFilters)}
      >
        {!hideFilters ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
      <WeatherReflectionFilters
        control={control}
        watch={watch}
        handleSubmit={handleSubmit(onSubmit)}
        weatherConstraints={weatherConstraints}
        readyToShare={!!(timeline && timeline.length > 0)}
        traceIndex={traceIndex}
      />
      <WeatherReflectionConstraints
        weatherConstraints={weatherConstraints}
        setWeatherConstraints={setWeatherConstraints}
        hide={hideFilters}
      />
      {isSubmitted && !isLoading && (!timeline || timeline?.length === 0) && (
        <Grid
          item
          container
          justifyContent='center'
          alignItems='center'
          sx={{ height: '70vh' }}
          direction='column'
        >
          <Typography color={grey[500]}>
            No weather with associated traces found for the selected constraints
            and time frame.{' '}
          </Typography>
          <Typography color={grey[500]}>
            Please make sure the selected analyzer is paired with a weather
            location through a dish.
          </Typography>
        </Grid>
      )}
      <Grid container item sx={{ height: '100%' }}>
        {isLoading ? (
          <Grid
            item
            sx={{ height: '40vh' }}
            container
            justifyContent='center'
            alignItems='center'
            direction='column'
          >
            <CircularProgress />
            <Typography sx={{ pt: '2rem' }} color={grey[500]}>
              If you've selected multiple months of history, this could take a
              few seconds...
            </Typography>
          </Grid>
        ) : (
          timeline &&
          timeline.length > 0 && (
            <Grid
              container
              item
              component={Paper}
              sx={{ paddingBottom: '.2rem', height: '100%' }}
            >
              <Grid item container xs={7} direction='column'>
                <Grid item sx={{ maxWidth: '100%' }}>
                  <WeatherTraceDisplay
                    analyzerId={submittedFormValues.analyzerId}
                    snapshotId={submittedFormValues.snapshotId}
                    traceSample={currentTrace}
                    traceIndex={traceIndex}
                    timeline={timeline}
                    handleTraceIndexChanged={handleTraceIndexChanged}
                  />
                </Grid>
              </Grid>
              <Grid item container xs>
                {currentTrace && (
                  <SettingsTabPanel
                    selectedAnalyzerId={submittedFormValues.analyzerId}
                    selectedSnapshot={currentTrace}
                    selectedSnapshotId={submittedFormValues.snapshotId}
                    displayActiveSnapshotSelector={false}
                    editSnapshotsDisabled={true}
                    editMarkersDisabled={true}
                    selectedIndexPoint={traceIndex}
                    isHistoricalMode
                    traceMode={0}
                    currentTimestamp={currentTrace?.time}
                    // @ts-ignore
                    weatherConstraints={weatherConstraints}
                  />
                )}
              </Grid>
            </Grid>
          )
        )}
      </Grid>
    </Grid>
  );
};

export default WeatherReflectionPage;
